import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  VStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import {
  CarriersComparisonTable,
  MarketOptionsModal,
  MVRErrorAlert,
  RfqConfirmationModal,
  RfqErrorAlert,
  ValidationErrorsModal,
} from '../components/carriers';
import { useCarrierSorting } from '../hooks';
import AllCoveragesModal from '../components/AllCoveragesModal';
import {
  apiSubmitAutoQuoteRfq,
  apiUpdateSelectedMarkets,
  apiSelectCarrierQuote,
  apiUpdateVehicleCoverages,
} from 'api/quote-marketplace';
import type {
  AutoCarriersData,
  CarriersVehicle,
  MVRErrorDetails,
  VehicleCoverages,
} from 'api/quote-marketplace';

interface UnconfirmedVehicle {
  id: number;
  label: string;
  missingAccidentBenefits: boolean;
  missingAllCoverages: boolean;
}

const getUnconfirmedVehicles = (
  vehicles: CarriersVehicle[],
): UnconfirmedVehicle[] => {
  return vehicles
    .filter((v) => {
      const coverages = v.coverages;
      return (
        !coverages?.accident_benefits_confirmed ||
        !coverages?.all_coverages_confirmed
      );
    })
    .map((v) => ({
      id: v.id,
      label: [v.vehicle_model_year, v.make, v.model].filter(Boolean).join(' '),
      missingAccidentBenefits: !v.coverages?.accident_benefits_confirmed,
      missingAllCoverages: !v.coverages?.all_coverages_confirmed,
    }));
};

interface DriverReportsState {
  isValidating: boolean;
  status: string | null;
  errorDetails: MVRErrorDetails | null;
  canRetry: boolean;
  retryValidation: () => Promise<void>;
}

interface AutoCarriersStepProps {
  data: AutoCarriersData;
  quoteId: number;
  onNavigateToTab: (tab: string) => void;
  onRefetch: () => void;
  driverReports?: DriverReportsState;
  autoGetQuotes?: boolean;
  onAutoGetQuotesHandled?: () => void;
}

const AutoCarriersStep = ({
  data,
  quoteId,
  onNavigateToTab,
  onRefetch,
  driverReports,
  autoGetQuotes,
  onAutoGetQuotesHandled,
}: AutoCarriersStepProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editingVehicle, setEditingVehicle] = useState<CarriersVehicle | null>(
    null,
  );
  const {
    isOpen: isValidationModalOpen,
    onOpen: openValidationModal,
    onClose: closeValidationModal,
  } = useDisclosure();
  const {
    isOpen: isMarketOptionsOpen,
    onOpen: openMarketOptions,
    onClose: closeMarketOptions,
  } = useDisclosure();
  const {
    isOpen: isCoveragesModalOpen,
    onOpen: openCoveragesModal,
    onClose: closeCoveragesModal,
  } = useDisclosure();
  const {
    isOpen: isRfqConfirmationOpen,
    onOpen: openRfqConfirmation,
    onClose: closeRfqConfirmation,
  } = useDisclosure();

  const isValidating = driverReports?.isValidating ?? false;

  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const submitRfqMutation = useMutation({
    mutationFn: () => apiSubmitAutoQuoteRfq(quoteId),
    onSuccess: () => {
      toast({
        title: 'Quote request submitted',
        description: 'Fetching quotes from carriers...',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to submit quote request',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const updateSelectedMarketsMutation = useMutation({
    mutationFn: (selectedMarkets: string[]) =>
      apiUpdateSelectedMarkets(quoteId, selectedMarkets),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['quote-marketplace', 'validation-schema', quoteId],
      });
      queryClient.invalidateQueries({
        queryKey: ['market-credentials', quoteId],
      });
      toast({
        title: 'Market selection updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
      closeMarketOptions();
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update market selection',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const selectCarrierMutation = useMutation({
    mutationFn: (carrierQuoteId: number) =>
      apiSelectCarrierQuote(quoteId, carrierQuoteId),
    onSuccess: () => {
      toast({
        title: 'Carrier selected',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries({
        queryKey: ['auto-quote-summary', quoteId],
      });
      queryClient.invalidateQueries({ queryKey: ['quote-group-summary'] });
      onRefetch();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to select carrier',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const updateCoveragesMutation = useMutation({
    mutationFn: ({
      vehicleId,
      coverages,
    }: {
      vehicleId: number;
      coverages: VehicleCoverages;
    }) => apiUpdateVehicleCoverages(quoteId, vehicleId, coverages),
    onSuccess: () => {
      closeCoveragesModal();
      setEditingVehicle(null);
      submitRfqMutation.mutate();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update coverages',
        description: error?.response?.data?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const unconfirmedVehicles = getUnconfirmedVehicles(data.vehicles);
  const hasUnconfirmedVehicles = unconfirmedVehicles.length > 0;

  const handleGetQuotesClick = () => {
    if (!data.is_valid_for_rfq || hasUnconfirmedVehicles) {
      openValidationModal();
      return;
    }
    openRfqConfirmation();
  };

  const handleConfirmRfq = () => {
    submitRfqMutation.mutate();
    closeRfqConfirmation();
  };

  const handleMarketOptionsApply = (selectedMarkets: string[]) => {
    updateSelectedMarketsMutation.mutate(selectedMarkets);
  };

  const handleSelectCarrier = (carrierQuoteId: number) => {
    selectCarrierMutation.mutate(carrierQuoteId);
  };

  const hasError = data.rfq_status === 'error';
  const isCapturingRiskDetails = data.risk_details_sync?.status === 'pending';
  const isRiskDetailsUpdateAhead =
    isCapturingRiskDetails || Boolean(data.risk_details_sync?.queued);
  const hasRiskDetailsSyncError =
    !hasError && data.risk_details_sync?.status === 'error';
  const canSubmit =
    data.rfq_status === 'not_started' ||
    data.rfq_status === 'shell_created' ||
    data.rfq_status === 'error' ||
    data.rfq_status === 'complete';
  const hasSelectedCarrier = data.selected_carrier_quote_id !== null;

  const handleEditCoverages = (vehicleId?: number) => {
    const targetVehicle = vehicleId
      ? data.vehicles.find((v) => v.id === vehicleId)
      : data.vehicles[0];

    if (targetVehicle) {
      setEditingVehicle(targetVehicle);
      openCoveragesModal();
    }
  };

  const handleSaveCoverages = (
    newCoverages: VehicleCoverages,
    _confirmed: boolean,
  ) => {
    if (!editingVehicle) return;

    updateCoveragesMutation.mutate({
      vehicleId: editingVehicle.id,
      coverages: newCoverages,
    });
  };

  const handleCloseCoveragesModal = () => {
    closeCoveragesModal();
    setEditingVehicle(null);
  };

  const filteredCarriers =
    data.selected_markets.length > 0
      ? data.available_carriers.filter((c) =>
          data.selected_markets.includes(c.product),
        )
      : data.available_carriers;

  const { sortBy, setSortBy, sortedCarriers } = useCarrierSorting({
    carriers: filteredCarriers,
    quotes: data.carrier_quotes,
    vehicles: data.vehicles,
    selectedCarrierQuoteId: data.selected_carrier_quote_id,
  });

  useEffect(() => {
    if (!autoGetQuotes) return;
    onAutoGetQuotesHandled?.();

    // An update still on its way to Acturis would hold the request back, the broker clicks Get Quotes once it is done
    if (isRiskDetailsUpdateAhead) return;

    if (!data.is_valid_for_rfq || hasUnconfirmedVehicles) {
      openValidationModal();
      return;
    }
    if (canSubmit && filteredCarriers.length > 0 && data.vehicles.length > 0) {
      submitRfqMutation.mutate();
    }
  }, [
    autoGetQuotes,
    onAutoGetQuotesHandled,
    isRiskDetailsUpdateAhead,
    data.is_valid_for_rfq,
    data.vehicles.length,
    hasUnconfirmedVehicles,
    canSubmit,
    filteredCarriers.length,
    openValidationModal,
    submitRfqMutation,
  ]);

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        {!data.is_valid_for_rfq && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Missing Required Information</AlertTitle>
              <AlertDescription>
                Some required fields are missing. Please complete all required
                information before requesting quotes.
                <Button
                  ml={2}
                  size="sm"
                  variant="link"
                  colorScheme="orange"
                  onClick={openValidationModal}
                >
                  View details
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {hasError && <RfqErrorAlert error={data.rfq_error} />}
        {hasRiskDetailsSyncError && (
          <RfqErrorAlert error={data.risk_details_sync.error} />
        )}

        {filteredCarriers.length === 0 ? (
          <VStack spacing={4} align="start">
            <Text color={labelColor}>
              No carriers selected. Click "Market Options" to select carriers.
            </Text>
            <Button size="sm" variant="outline" onClick={openMarketOptions}>
              Market Options
            </Button>
          </VStack>
        ) : data.vehicles.length === 0 ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>No vehicles found</AlertTitle>
              <AlertDescription>
                Please add vehicles before requesting quotes.
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <CarriersComparisonTable
            availableCarriers={sortedCarriers}
            carrierQuotes={data.carrier_quotes}
            vehicles={data.vehicles}
            selectedCarrierQuoteId={data.selected_carrier_quote_id}
            rfqStatus={data.rfq_status}
            onSelectCarrier={handleSelectCarrier}
            onEditCoverages={handleEditCoverages}
            onOpenMarketOptions={openMarketOptions}
            onGetQuotes={handleGetQuotesClick}
            isSelectingCarrier={selectCarrierMutation.isPending}
            isGettingQuotes={submitRfqMutation.isPending}
            canGetQuotes={
              canSubmit &&
              data.is_valid_for_rfq &&
              !hasUnconfirmedVehicles &&
              !isCapturingRiskDetails
            }
            isCapturingRiskDetails={isCapturingRiskDetails}
            riskDetailsSync={data.risk_details_sync}
            provinceCode={data.province}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />
        )}

        {isValidating && (
          <Alert status="info" borderRadius="md">
            <Spinner size="sm" mr={3} />
            <Box>
              <AlertTitle>Validating Driver Information</AlertTitle>
              <AlertDescription>
                Ordering MVR and DASH reports to verify driver history...
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {driverReports?.errorDetails &&
          (driverReports.status === 'error' || driverReports.canRetry) && (
            <MVRErrorAlert
              errorDetails={driverReports.errorDetails}
              canRetry={driverReports.canRetry}
              onRetry={driverReports.retryValidation}
              isRetrying={driverReports.isValidating}
            />
          )}
      </VStack>

      <ValidationErrorsModal
        isOpen={isValidationModalOpen}
        onClose={closeValidationModal}
        errors={data.validation_errors}
        unconfirmedVehicles={unconfirmedVehicles}
        onNavigateToTab={onNavigateToTab}
      />

      <MarketOptionsModal
        isOpen={isMarketOptionsOpen}
        onClose={closeMarketOptions}
        availableCarriers={data.available_carriers}
        selectedMarkets={data.selected_markets}
        onApply={handleMarketOptionsApply}
        isLoading={updateSelectedMarketsMutation.isPending}
      />

      <RfqConfirmationModal
        isOpen={isRfqConfirmationOpen}
        onClose={closeRfqConfirmation}
        onConfirm={handleConfirmRfq}
        selectedCarriers={filteredCarriers}
        isLoading={submitRfqMutation.isPending}
        hasSelectedCarrier={hasSelectedCarrier}
      />

      {editingVehicle && (
        <AllCoveragesModal
          isOpen={isCoveragesModalOpen}
          onClose={handleCloseCoveragesModal}
          onSave={handleSaveCoverages}
          initialCoverages={editingVehicle.coverages}
          vehicleLabel={`${editingVehicle.vehicle_model_year || ''} ${editingVehicle.make} ${editingVehicle.model}`.trim()}
          quoteId={quoteId}
          vehicleVin={editingVehicle.vin}
          vehicleId={editingVehicle.id}
          provinceCode={data.province}
        />
      )}
    </Box>
  );
};

export default AutoCarriersStep;
